import React, { useState } from 'react'
import { useNavigate, Link, useSearchParams } from 'react-router-dom'
import { Eye, EyeOff } from 'lucide-react'
import { useAuthStore } from '@/store/authStore'
import { ArTechLogo } from '@/components/ui/ArTechLogo'

// Friendly messages for the ?error= codes the Microsoft SSO callback redirects with.
const SSO_ERRORS: Record<string, string> = {
  sso_nouser: 'No CRM account is linked to that Microsoft email. Please contact your administrator.',
  sso_denied: 'Microsoft sign-in was cancelled.',
  sso_failed: 'Microsoft sign-in failed. Please try again.',
  sso_state: 'Your sign-in session expired. Please try again.',
  sso_unconfigured: 'Microsoft sign-in is not configured yet.',
}

// The Microsoft 4-square logo (decorative — the button text carries the meaning).
function MicrosoftLogo() {
  return (
    <svg width="18" height="18" viewBox="0 0 21 21" aria-hidden="true" focusable="false">
      <rect x="1" y="1" width="9" height="9" fill="#F25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
      <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
      <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
    </svg>
  )
}

const C = {
  navy:  '#0D1F4E',
  blue:  '#1A6AB4',
  teal:  '#3DC7B3',
  green: '#2DB37A',
  cloud: '#F4F8FF',
  mist:  '#E8EDF5',
  steel: '#A0AABF',
} as const

const FEATURES = [
  { num: '01', title: 'Lead Management',    desc: 'Capture, track and convert sales leads effortlessly'   },
  { num: '02', title: 'Deal Pipeline',       desc: 'Visualise and accelerate every stage of your deals'    },
  { num: '03', title: 'Contact Management',  desc: 'Build and nurture lasting client relationships'         },
  { num: '04', title: 'Reports & Analytics', desc: 'Data-driven insights to power smarter decisions'       },
  { num: '05', title: 'Team Collaboration',  desc: 'Align your sales team and hit targets together'        },
  { num: '06', title: 'Email Integration',   desc: 'Connect Outlook — sync and send directly from CRM'    },
] as const

const FEAT_ICONS = ['🎯', '💼', '👥', '📊', '🤝', '📧']

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

  .lp {
    display: flex; flex-direction: row; height: 100vh; overflow: hidden;
    background: ${C.navy}; font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
  }

  /* ── Left panel ── */
  .lp-left {
    position: relative; width: 100%;
    transition: width 0.92s cubic-bezier(0.77,0,0.175,1);
    overflow: hidden; flex-shrink: 0;
  }
  .lp-left.revealed { width: 58%; }

  .lp-video {
    position: absolute; inset: 0; width: 100%; height: 100%;
    object-fit: cover; z-index: 0;
  }
  .lp-video-tint {
    position: absolute; inset: 0;
    background: linear-gradient(135deg, rgba(13,31,78,0.82) 0%, rgba(13,31,78,0.65) 50%, rgba(26,106,180,0.55) 100%);
    z-index: 1;
  }
  .lp-left::after {
    content: ''; position: absolute; inset: 0;
    background-image: radial-gradient(circle, rgba(61,199,179,0.06) 1px, transparent 1px);
    background-size: 48px 48px; z-index: 2; pointer-events: none;
  }

  .blob { position: absolute; border-radius: 50%; filter: blur(60px); opacity: 0.18; z-index: 2; }
  .blob-1 {
    width: 420px; height: 420px;
    background: radial-gradient(circle, ${C.teal}, transparent 70%);
    top: -120px; left: -80px; animation: meshMove1 18s ease-in-out infinite;
  }
  .blob-2 {
    width: 320px; height: 320px;
    background: radial-gradient(circle, ${C.blue}, transparent 70%);
    bottom: 80px; right: -60px; animation: meshMove2 22s ease-in-out infinite;
  }
  .blob-3 {
    width: 260px; height: 260px;
    background: radial-gradient(circle, ${C.green}, transparent 70%);
    bottom: -60px; left: 40%; animation: meshMove3 14s ease-in-out infinite;
  }

  /* ── Hero overlay ── */
  .lp-hero {
    position: absolute; inset: 0; z-index: 10;
    display: flex; flex-direction: column; align-items: center; justify-content: center;
    text-align: center; padding: 48px 40px;
    transition: opacity 0.45s ease;
  }
  .lp-hero.hidden { opacity: 0; pointer-events: none; }

  .lp-hero-title {
    font-size: 2.6rem; font-weight: 800; color: #fff; line-height: 1.15;
    margin: 20px 0 12px; animation: heroUp 0.7s ease both;
  }
  .lp-hero-sub {
    font-size: 1rem; color: rgba(255,255,255,0.72);
    max-width: 420px; margin: 0 auto 32px;
    animation: heroUp 0.7s 0.1s ease both; line-height: 1.6;
  }

  .lp-stats { display: flex; gap: 12px; margin-bottom: 36px; animation: heroUp 0.7s 0.2s ease both; }
  .lp-stat {
    background: rgba(255,255,255,0.10); backdrop-filter: blur(12px);
    border: 1px solid rgba(255,255,255,0.20); border-radius: 12px;
    padding: 14px 18px; text-align: center;
    transition: transform 0.2s; cursor: default;
  }
  .lp-stat:hover { transform: translateY(-4px); }
  .lp-stat-val { font-size: 1.5rem; font-weight: 800; line-height: 1; margin-bottom: 4px; }
  .lp-stat-label { font-size: 0.68rem; color: rgba(255,255,255,0.60); text-transform: uppercase; letter-spacing: 0.08em; }

  .lp-cta {
    display: inline-flex; align-items: center; gap: 10px;
    padding: 14px 32px; background: linear-gradient(135deg, ${C.teal}, ${C.blue});
    color: #fff; font-weight: 700; font-size: 0.95rem;
    border: none; border-radius: 50px; cursor: pointer; font-family: inherit;
    animation: heroUp 0.7s 0.3s ease both, pulseGlow 2.8s 1.2s ease-in-out infinite;
    transition: transform 0.2s, box-shadow 0.2s;
  }
  .lp-cta:hover { transform: scale(1.04); box-shadow: 0 8px 30px rgba(61,199,179,0.45); }

  /* ── Features panel (after reveal) ── */
  .lp-left-inner {
    position: absolute; inset: 0; z-index: 10;
    display: flex; flex-direction: column; justify-content: center;
    padding: 36px; opacity: 0; pointer-events: none;
    transition: opacity 0.5s 0.6s ease; overflow-y: auto;
  }
  .lp-left-inner.visible { opacity: 1; pointer-events: all; }

  .lp-inner-title { font-size: 1.35rem; font-weight: 700; color: #fff; margin: 0 0 6px; }
  .lp-inner-sub   { font-size: 0.82rem; color: rgba(255,255,255,0.55); margin: 0 0 20px; }

  .lp-feats { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 24px; }
  .lp-feat {
    background: rgba(255,255,255,0.08); backdrop-filter: blur(12px);
    border: 1px solid rgba(255,255,255,0.14); border-radius: 12px;
    padding: 13px 14px; display: flex; align-items: flex-start; gap: 10px;
    animation: slideRight 0.5s ease both;
    transition: transform 0.2s, background 0.2s;
  }
  .lp-feat:hover { transform: translateY(-2px); background: rgba(255,255,255,0.13); }
  .lp-feat-icon {
    width: 32px; height: 32px; border-radius: 8px;
    display: flex; align-items: center; justify-content: center;
    font-size: 15px; flex-shrink: 0;
  }
  .lp-feat-num   { font-size: 0.62rem; font-weight: 700; color: rgba(255,255,255,0.35); letter-spacing: 0.1em; margin-bottom: 2px; }
  .lp-feat-title { font-size: 0.8rem;  font-weight: 700; color: #fff; margin-bottom: 2px; }
  .lp-feat-desc  { font-size: 0.68rem; color: rgba(255,255,255,0.52); line-height: 1.4; }

  .lp-stats-row { display: flex; gap: 10px; }

  /* ── Right panel ── */
  .lp-right {
    width: 0; overflow: hidden;
    transition: width 0.92s cubic-bezier(0.77,0,0.175,1);
    background: ${C.cloud}; position: relative; flex-shrink: 0;
    display: flex; align-items: center; justify-content: center;
  }
  .lp-right.revealed { width: 42%; }
  .lp-right::before {
    content: ''; position: absolute; inset: 0;
    background-image: radial-gradient(circle, rgba(26,106,180,0.05) 1px, transparent 1px);
    background-size: 28px 28px; pointer-events: none;
  }

  /* ── Form card ── */
  .lp-card {
    position: relative; width: 420px; max-width: 90%;
    background: #fff; border-radius: 20px;
    box-shadow: 0 20px 60px rgba(26,106,180,0.14), 0 4px 16px rgba(0,0,0,0.06);
    overflow: hidden; animation: fadeUp 0.55s 0.35s ease both; z-index: 2;
  }
  .lp-card-bar {
    height: 5px;
    background: linear-gradient(90deg, ${C.blue}, ${C.teal}, ${C.green});
    animation: shimmerBar 0.8s 0.4s ease both; transform-origin: left;
  }
  .lp-card-inner  { padding: 28px 32px 24px; }
  .lp-card-header { text-align: center; margin-bottom: 24px; display: flex; flex-direction: column; align-items: center; }
  .lp-card-header h2 { font-size: 1.35rem; font-weight: 800; color: ${C.navy}; margin: 14px 0 5px; }
  .lp-card-header p  { font-size: 0.82rem; color: ${C.steel}; margin: 0; }

  .lp-field { margin-bottom: 18px; }
  .lp-label {
    display: block; font-size: 0.7rem; font-weight: 600;
    text-transform: uppercase; letter-spacing: 0.08em;
    color: rgba(13,31,78,0.60); margin-bottom: 6px;
  }
  .lp-input {
    width: 100%; padding: 11px 14px; font-size: 0.88rem;
    background: ${C.cloud}; border: 1.5px solid ${C.mist};
    border-radius: 10px; color: ${C.navy}; outline: none;
    transition: border-color 0.2s, box-shadow 0.2s;
    box-sizing: border-box; font-family: inherit;
  }
  .lp-input:focus { border-color: ${C.blue}; box-shadow: 0 0 0 3px rgba(26,106,180,0.15); background: #fff; }
  .lp-input::placeholder { color: ${C.steel}; }
  .lp-input.lp-inp-err { border-color: #DC2626; }
  .lp-input-wrap { position: relative; }
  .lp-eye {
    position: absolute; right: 12px; top: 50%; transform: translateY(-50%);
    background: none; border: none; cursor: pointer; color: ${C.steel};
    padding: 0; display: flex; align-items: center;
  }
  .lp-eye:hover { color: ${C.navy}; }

  .lp-row { display: flex; align-items: center; justify-content: space-between; margin-bottom: 22px; }
  .lp-check { display: flex; align-items: center; gap: 8px; cursor: pointer; }
  .lp-check input { width: 15px; height: 15px; accent-color: ${C.blue}; cursor: pointer; }
  .lp-check span { font-size: 0.78rem; color: #4B5563; }
  .lp-forgot { font-size: 0.78rem; font-weight: 600; color: ${C.blue}; text-decoration: none; }
  .lp-forgot:hover { text-decoration: underline; }

  .lp-btn {
    width: 100%; padding: 13px;
    background: linear-gradient(135deg, ${C.navy} 0%, ${C.blue} 100%);
    color: #fff; border: none; border-radius: 10px;
    font-size: 0.92rem; font-weight: 700; cursor: pointer; font-family: inherit;
    transition: opacity 0.2s, transform 0.15s;
    display: flex; align-items: center; justify-content: center; gap: 8px;
  }
  .lp-btn:hover:not(:disabled) { opacity: 0.91; transform: translateY(-1px); }
  .lp-btn:active:not(:disabled) { transform: translateY(0); }
  .lp-btn:disabled { opacity: 0.65; cursor: not-allowed; }

  .lp-error {
    background: #FEF2F2; border: 1px solid #FECACA; border-radius: 8px;
    padding: 10px 14px; font-size: 0.8rem; color: #DC2626; margin-bottom: 18px;
  }
  .lp-field-err { font-size: 0.72rem; color: #DC2626; margin-top: 4px; }

  .lp-card-footer {
    border-top: 1px solid ${C.mist}; padding: 14px 32px;
    display: flex; align-items: center; justify-content: center; gap: 8px;
  }
  .lp-card-footer span { font-size: 0.74rem; color: ${C.steel}; }

  /* ── "OR" divider + Microsoft SSO ── */
  .lp-divider {
    display: flex; align-items: center; gap: 14px;
    margin: 20px 0 16px; color: ${C.steel};
    font-size: 0.7rem; font-weight: 700; letter-spacing: 0.1em;
  }
  .lp-divider::before, .lp-divider::after {
    content: ''; flex: 1; height: 1px; background: ${C.mist};
  }
  .lp-sso {
    width: 100%; padding: 13px; min-height: 48px;
    display: flex; align-items: center; justify-content: center; gap: 10px;
    background: #fff; border: 1.5px solid ${C.mist}; border-radius: 10px;
    font-size: 0.9rem; font-weight: 600; color: ${C.navy};
    cursor: pointer; font-family: inherit;
    transition: border-color 0.2s, box-shadow 0.2s, background 0.2s;
  }
  .lp-sso:hover:not(:disabled) { border-color: ${C.blue}; box-shadow: 0 2px 10px rgba(26,106,180,0.12); background: ${C.cloud}; }
  .lp-sso:focus-visible { outline: 3px solid rgba(26,106,180,0.40); outline-offset: 2px; }
  .lp-sso:disabled { opacity: 0.6; cursor: not-allowed; }

  /* ── Keyframes ── */
  @keyframes meshMove1 {
    0%,100% { transform: translate(0,0) scale(1); }
    33%  { transform: translate(40px,-30px) scale(1.1); }
    66%  { transform: translate(-20px,50px) scale(0.9); }
  }
  @keyframes meshMove2 {
    0%,100% { transform: translate(0,0) scale(1); }
    40%  { transform: translate(-50px,40px) scale(1.15); }
    70%  { transform: translate(30px,-20px) scale(0.92); }
  }
  @keyframes meshMove3 {
    0%,100% { transform: translate(0,0) scale(1); }
    50%  { transform: translate(60px,30px) scale(1.08); }
  }
  @keyframes shimmerBar  { from { transform: scaleX(0); } to { transform: scaleX(1); } }
  @keyframes heroUp      { from { opacity: 0; transform: translateY(28px); } to { opacity: 1; transform: translateY(0); } }
  @keyframes fadeUp      { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
  @keyframes slideRight  { from { opacity: 0; transform: translateX(-12px); } to { opacity: 1; transform: translateX(0); } }
  @keyframes pulseGlow   { 0%,100% { box-shadow: 0 0 0 0 rgba(61,199,179,0); } 50% { box-shadow: 0 0 0 12px rgba(61,199,179,0.18); } }
  @keyframes lpSpin      { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

  /* ── Responsive ── */
  @media (max-width: 767px) {
    .lp-left.revealed  { width: 0; }
    .lp-right.revealed { width: 100%; }
    .lp-card { width: 340px; }
    .lp-card-inner  { padding: 24px 20px 20px; }
    .lp-card-footer { padding: 12px 20px; }
  }
`

export default function LoginPage() {
  const navigate = useNavigate()
  const { login, isLoading, error, clearError } = useAuthStore()
  const [searchParams] = useSearchParams()
  const ssoError = searchParams.get('error')
  const ssoMessage = ssoError ? SSO_ERRORS[ssoError] || 'Sign-in failed. Please try again.' : null

  const [revealed, setRevealed] = useState(false)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [rememberMe, setRememberMe] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [fieldErrors, setFieldErrors] = useState<{ email?: string; password?: string }>({})

  React.useEffect(() => { clearError() }, [])

  function validate(): boolean {
    const errs: { email?: string; password?: string } = {}
    if (!email.trim()) errs.email = 'Email is required'
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) errs.email = 'Please enter a valid email address'
    if (!password) errs.password = 'Password is required'
    else if (password.length < 6) errs.password = 'Password must be at least 6 characters'
    setFieldErrors(errs)
    return Object.keys(errs).length === 0
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    clearError()
    if (!validate()) return
    try {
      await login({ email: email.trim(), password, rememberMe })
      navigate('/home', { replace: true })
    } catch {
      // error shown via useAuthStore.error
    }
  }

  function handleMicrosoftLogin() {
    // Full-page redirect to the backend, which sends the browser on to Microsoft.
    // (Dev: the /api proxy forwards to the backend; prod: same origin.)
    window.location.href = '/api/v1/auth/microsoft/login'
  }

  const iconBg = (i: number) =>
    i % 2 === 0 ? `${C.teal}22` : `${'#6EB5E8'}22`

  return (
    <div className="lp">
      <style dangerouslySetInnerHTML={{ __html: STYLES }} />

      {/* ══════════════════════ LEFT PANEL ══════════════════════ */}
      <div className={`lp-left${revealed ? ' revealed' : ''}`}>
        <video className="lp-video" autoPlay loop muted playsInline>
          <source src="/login-bg.mp4" type="video/mp4" />
        </video>
        <div className="lp-video-tint" />
        <div className="blob blob-1" />
        <div className="blob blob-2" />
        <div className="blob blob-3" />

        {/* Hero — shown before CTA click */}
        <div className={`lp-hero${revealed ? ' hidden' : ''}`}>
          <ArTechLogo size="lg" showText showTagline />
          <h1 className="lp-hero-title">AR SalesMine</h1>
          <p className="lp-hero-sub">
            Your complete sales platform — leads, deals, contacts,<br />
            and analytics all in one place.
          </p>
          <button className="lp-cta" onClick={() => setRevealed(true)}>
            Get Started &rarr;
          </button>
        </div>

        {/* Features panel — shown after CTA click */}
        <div className={`lp-left-inner${revealed ? ' visible' : ''}`}>
          <p className="lp-inner-title">Everything your sales team needs</p>
          <p className="lp-inner-sub">One platform. Every tool. Zero complexity.</p>
          <div className="lp-feats">
            {FEATURES.map((f, i) => (
              <div
                key={f.num}
                className="lp-feat"
                style={{ animationDelay: `${i * 0.07}s` }}
              >
                <div className="lp-feat-icon" style={{ background: iconBg(i) }}>
                  <span>{FEAT_ICONS[i]}</span>
                </div>
                <div>
                  <div className="lp-feat-num">{f.num}</div>
                  <div className="lp-feat-title">{f.title}</div>
                  <div className="lp-feat-desc">{f.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ══════════════════════ RIGHT PANEL ══════════════════════ */}
      <div className={`lp-right${revealed ? ' revealed' : ''}`}>
        <div className="lp-card">
          {/* Gradient accent bar */}
          <div className="lp-card-bar" />

          <div className="lp-card-inner">
            {/* Card header */}
            <div className="lp-card-header">
              <ArTechLogo size="login" />
              <h2>Welcome back</h2>
              <p>Sign in to your AR SalesMine account</p>
            </div>

            {/* API / SSO error */}
            {(error || ssoMessage) && (
              <div className="lp-error" role="alert">{error || ssoMessage}</div>
            )}

            <form onSubmit={handleSubmit} noValidate>
              {/* Email */}
              <div className="lp-field">
                <label className="lp-label" htmlFor="email">
                  Email Address <span style={{ color: '#DC2626' }}>*</span>
                </label>
                <input
                  id="email"
                  type="text"
                  autoComplete="username"
                  value={email}
                  onChange={e => {
                    setEmail(e.target.value)
                    setFieldErrors(p => ({ ...p, email: undefined }))
                    clearError()
                  }}
                  className={`lp-input${fieldErrors.email ? ' lp-inp-err' : ''}`}
                  placeholder="you@artechsolution.co.in"
                />
                {fieldErrors.email && (
                  <p className="lp-field-err">{fieldErrors.email}</p>
                )}
              </div>

              {/* Password */}
              <div className="lp-field">
                <label className="lp-label" htmlFor="password">
                  Password <span style={{ color: '#DC2626' }}>*</span>
                </label>
                <div className="lp-input-wrap">
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    value={password}
                    onChange={e => {
                      setPassword(e.target.value)
                      setFieldErrors(p => ({ ...p, password: undefined }))
                      clearError()
                    }}
                    className={`lp-input${fieldErrors.password ? ' lp-inp-err' : ''}`}
                    style={{ paddingRight: '42px' }}
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    className="lp-eye"
                    onClick={() => setShowPassword(v => !v)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
                {fieldErrors.password && (
                  <p className="lp-field-err">{fieldErrors.password}</p>
                )}
              </div>

              {/* Remember me + Forgot password */}
              <div className="lp-row">
                <label className="lp-check">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={e => setRememberMe(e.target.checked)}
                  />
                  <span>Remember me</span>
                </label>
                <Link to="/forgot-password" className="lp-forgot">
                  Forgot password?
                </Link>
              </div>

              {/* Submit */}
              <button type="submit" className="lp-btn" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <span style={{
                      width: 16, height: 16, display: 'inline-block',
                      border: '2px solid rgba(255,255,255,0.35)',
                      borderTopColor: '#fff', borderRadius: '50%',
                      animation: 'lpSpin 0.8s linear infinite',
                    }} />
                    Signing in…
                  </>
                ) : 'Sign In →'}
              </button>
            </form>

            {/* Divider */}
            <div className="lp-divider" aria-hidden="true">OR</div>

            {/* Microsoft SSO */}
            <button
              type="button"
              className="lp-sso"
              onClick={handleMicrosoftLogin}
              disabled={isLoading}
            >
              <MicrosoftLogo />
              Continue with Microsoft
            </button>
          </div>

          {/* Card footer */}
          <div className="lp-card-footer">
            <ArTechLogo size="sm" />
            <span>Powered by AR Tech Solutions</span>
          </div>
        </div>
      </div>
    </div>
  )
}
